import { Contract, ContractRevision } from './contracts.service';

/** Tariff string that marks a free-trial revision (matches backend `Tariff.TRIAL`). */
export const TRIAL_TARIFF = 'TRIAL';

/** Plan level that marks a free trial (matches backend level-6 `TRIAL`). */
export const TRIAL_LEVEL = 6;

/** Max number of times a free trial may be extended (3 TRIAL revisions). */
export const MAX_TRIAL_EXTENSIONS = 3;

export type TrialState = 'active' | 'expired';

export interface TrialInfo {
  /**
   * True when the LATEST revision (highest startDate) runs on the TRIAL tariff.
   * By definition this means there is no follow-up (paid) revision after it.
   */
  isFreeTrial: boolean;
  /** Only meaningful when `isFreeTrial`. `'expired'` if the contract is deactivated or the trial end is in the past. */
  trialState: TrialState;
  /** Number of TRIAL revisions on the contract — drives the 3x cap. */
  trialExtensionCount: number;
  /** End date of the latest (trial) revision, or null for an open-ended trial. */
  currentEnd: string | null;
}

/**
 * Today as `YYYY-MM-DD` in LOCAL time (our servers + operators are Berlin).
 * Deliberately not `toISOString()` — that yields the UTC date, which is the
 * previous day for ~2h around midnight in Berlin (UTC+1/+2). Injectable for tests.
 */
export function todayIso(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Pure free-trial detector. Given a contract's revisions (and the contract's
 * `deactivated` flag), derive whether this is an extendable free trial, its
 * state, and how many times it has already been extended.
 *
 * Trial check uses `tariff === 'TRIAL'` (there is no `level` field on the mapped
 * revision) — consistent with the backend `Tariff.TRIAL` guard.
 *
 * NOTE: `contract.deactivated` is deliberately NOT used. On the contract DTO
 * `deactivated = (endDate != null)`, so it is `true` for EVERY bounded trial —
 * including an active one ending in the future. Expiry is therefore derived
 * purely from the trial revision's end date.
 *
 * @param revisions  the contract's revisions (any order)
 * @param today  override for "today" (`YYYY-MM-DD`), for tests
 */
export function detectTrial(
  revisions: ContractRevision[],
  today: string = todayIso(),
): TrialInfo {
  // The signup is the first TRIAL revision and is NOT an extension; each later TRIAL revision is one extension.
  // Mirrors the backend cap (prior extensions = TRIAL revision count - 1, max MAX_TRIAL_EXTENSIONS).
  const trialExtensionCount = Math.max(0, revisions.filter((r) => r.tariff === TRIAL_TARIFF).length - 1);

  if (revisions.length === 0) {
    return { isFreeTrial: false, trialState: 'expired', trialExtensionCount: 0, currentEnd: null };
  }

  // Latest revision = highest startDate. ISO date strings sort lexically.
  const latest = revisions.reduce((acc, r) => (r.startDate > acc.startDate ? r : acc), revisions[0]);

  const isFreeTrial = latest.tariff === TRIAL_TARIFF;
  if (!isFreeTrial) {
    return { isFreeTrial: false, trialState: 'expired', trialExtensionCount, currentEnd: null };
  }

  // Open-ended trial (endDate null) is always active. Otherwise expired only when
  // the trial end date is strictly in the past (NOT from contract.deactivated,
  // which is true for every bounded trial — see note above).
  const expired = latest.endDate !== null && latest.endDate < today;

  return {
    isFreeTrial: true,
    trialState: expired ? 'expired' : 'active',
    trialExtensionCount,
    currentEnd: latest.endDate,
  };
}

/**
 * Lightweight free-trial detector from the contract DTO alone (no revisions).
 *
 * The contract-list endpoint already carries `plan.level` and `endDate`, so the
 * always-visible header badge can be correct on first paint — WITHOUT waiting
 * for the lazily-loaded revisions. Using `detectTrial([])` before the revisions
 * arrive would wrongly fall back to the generic "Deactivated" badge, because the
 * contract DTO is `deactivated:true` for EVERY bounded trial (EB-3922 regression).
 *
 * `trialExtensionCount` is always 0 here — the precise count (for the 3x cap)
 * needs the revisions and is only relevant in the expanded actions column, where
 * `detectTrial` takes over once `revisionsLoaded()`.
 *
 * @param contract  the contract DTO from `getContracts`
 * @param today  override for "today" (`YYYY-MM-DD`), for tests
 */
export function detectTrialFromContract(
  contract: Contract,
  today: string = todayIso(),
): TrialInfo {
  const isFreeTrial = contract.plan?.level === TRIAL_LEVEL;
  if (!isFreeTrial) {
    return { isFreeTrial: false, trialState: 'expired', trialExtensionCount: 0, currentEnd: null };
  }

  const expired = contract.endDate !== null && contract.endDate < today;
  return {
    isFreeTrial: true,
    trialState: expired ? 'expired' : 'active',
    trialExtensionCount: 0,
    currentEnd: contract.endDate,
  };
}
